/**
 * ZAXIA Sustainability Impact Aggregator
 *
 * Phase P2.3-2: Sustainability Impact Dashboard
 *
 * Computes real environmental and operational impact metrics from EcoEvents.
 * Provides aggregated statistics for daily, weekly, monthly, and per-stay reporting.
 *
 * Baseline Constants (v1):
 * - FULL_CLEAN: 60L water, 2kWh energy, 0.6kg CO2
 * - TOWELS_ONLY: 15L water, 0.5kWh energy, 0.15kg CO2
 * - NO_SERVICE: 0L water, 0kWh energy, 0kg CO2
 * - Laundry cycle: 1.5kg CO2
 */

import type { EcoEvent } from './greenStay';
import { getAllEcoEvents } from './ecoLogger';

// =============================================================================
// PMS DATA SOURCE (CURRENT STATE)
// =============================================================================

// Use VITE_API_URL (strip trailing /api if present) for raw base URL
const _rawApiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
const API_BASE = _rawApiUrl.replace(/\/api\/?$/, '');

/**
 * Fetch currently occupied rooms with Green Stay opt-in from PMS (source of truth).
 * This provides real-time active participants, unlike localStorage eco events which include past guests.
 */
function countGreenStayParticipants(rooms: any[]): number {
  const activeParticipants = rooms.filter((room: any) => {
    const stay = room.stay;
    return stay && stay.greenPreferences?.greenStayOptIn === true;
  }).length;

  console.log(`[SustainabilityAggregator] Active Green Stay participants from PMS: ${activeParticipants}`);
  return activeParticipants;
}

/**
 * Calculate active savings from currently enrolled guests (PMS Green Stay preferences).
 * This shows COMMITTED savings from active guests, not historical events.
 */
function computeActiveSavings(rooms: any[]): SustainabilityMetrics {
  try {

    let waterSaved = 0;
    let energySaved = 0;
    let co2Reduced = 0;
    let laundryCyclesAvoided = 0;
    let cleaningsSkipped = 0;

    // Calculate savings from currently enrolled guests' Green Stay preferences
    for (const room of rooms) {
      const stay = room.stay;
      if (!stay || !stay.greenPreferences?.greenStayOptIn) continue;

      const greenPrefs = stay.greenPreferences;
      const skipDates = greenPrefs.skipCleaningDates || [];
      const towelDates = greenPrefs.towelsOnlyDates || [];

      // Count skip cleaning days (full clean avoided)
      const skipCount = Array.isArray(skipDates) ? skipDates.length : 0;
      waterSaved += skipCount * BASELINE_CONSTANTS.FULL_CLEAN.water;
      energySaved += skipCount * BASELINE_CONSTANTS.FULL_CLEAN.energy;
      co2Reduced += skipCount * BASELINE_CONSTANTS.FULL_CLEAN.co2;
      laundryCyclesAvoided += skipCount * BASELINE_CONSTANTS.FULL_CLEAN.laundry;
      cleaningsSkipped += skipCount;

      // Count towels-only days (partial savings vs full clean)
      const towelCount = Array.isArray(towelDates) ? towelDates.length : 0;
      const towelSavings = {
        water: BASELINE_CONSTANTS.FULL_CLEAN.water - BASELINE_CONSTANTS.TOWELS_ONLY.water,
        energy: BASELINE_CONSTANTS.FULL_CLEAN.energy - BASELINE_CONSTANTS.TOWELS_ONLY.energy,
        co2: BASELINE_CONSTANTS.FULL_CLEAN.co2 - BASELINE_CONSTANTS.TOWELS_ONLY.co2,
        laundry: BASELINE_CONSTANTS.FULL_CLEAN.laundry - BASELINE_CONSTANTS.TOWELS_ONLY.laundry,
      };
      waterSaved += towelCount * towelSavings.water;
      energySaved += towelCount * towelSavings.energy;
      co2Reduced += towelCount * towelSavings.co2;
      laundryCyclesAvoided += towelCount * towelSavings.laundry;
    }

    const costAvoided =
      waterSaved * COST_ESTIMATES.waterPerLiter +
      energySaved * COST_ESTIMATES.energyPerKwh +
      laundryCyclesAvoided * COST_ESTIMATES.laundryCycle;

    console.log(`[SustainabilityAggregator] Active savings from PMS: ${waterSaved}L water, ${cleaningsSkipped} cleanings`);

    const now = new Date();
    return {
      waterSaved,
      energySaved,
      co2Reduced,
      laundryCyclesAvoided,
      cleaningsSkipped,
      costAvoided,
      timeRange: { start: now, end: now, scope: 'today' },
    };
  } catch (error) {
    console.error('[SustainabilityAggregator] Failed to fetch active savings from PMS:', error);
    return getZeroMetrics();
  }
}

function getZeroMetrics(): SustainabilityMetrics {
  const now = new Date();
  return {
    waterSaved: 0,
    energySaved: 0,
    co2Reduced: 0,
    laundryCyclesAvoided: 0,
    cleaningsSkipped: 0,
    costAvoided: 0,
    timeRange: { start: now, end: now, scope: 'today' },
  };
}

// =============================================================================
// BASELINE CONSTANTS
// =============================================================================

/**
 * Baseline resource consumption estimates per service mode.
 * These are industry-standard approximations for hotel operations.
 *
 * v3: Aligned with AI assistant calculations for consistency.
 * - SKIP_CLEANING: Full service avoided (100L includes laundry + room cleaning water)
 * - TOWELS_ONLY: Partial service (70L baseline, saves 30L vs full clean)
 */
export const BASELINE_CONSTANTS = {
  FULL_CLEAN: {
    water: 100, // liters (includes laundry cycle water usage)
    energy: 2, // kWh
    co2: 0.6, // kg
    laundry: 1, // cycles
  },
  TOWELS_ONLY: {
    water: 70, // liters (housekeeping visit + towel laundry)
    energy: 1.2, // kWh
    co2: 0.4, // kg
    laundry: 0.7, // cycles (towel laundry still needed)
  },
  NO_SERVICE: {
    water: 0,
    energy: 0,
    co2: 0,
    laundry: 0,
  },
  LAUNDRY_CYCLE: {
    co2: 1.5, // kg per full cycle
  },
} as const;

/**
 * Cost estimates per unit (optional, for cost savings calculation)
 */
export const COST_ESTIMATES = {
  waterPerLiter: 0.002, // €/L
  energyPerKwh: 0.15, // €/kWh
  laundryCycle: 2.5, // € per cycle
} as const;

// =============================================================================
// TYPES
// =============================================================================

export type TimeScope = 'today' | 'week' | 'month' | 'all';

export interface SustainabilityMetrics {
  waterSaved: number; // liters
  energySaved: number; // kWh
  co2Reduced: number; // kg
  laundryCyclesAvoided: number;
  cleaningsSkipped: number;
  costAvoided: number; // € (optional)
  timeRange: {
    start: Date;
    end: Date;
    scope: TimeScope;
  };
}

export interface ParticipationMetrics {
  activeParticipants: number; // guests with eco mode ON
  totalGuests: number;
  participationRate: number; // percentage
  towelsOnlyAdoption: number; // count
  skipCleaningAdoption: number; // count
  repeatParticipation: number; // guests with multiple events
}

export interface TrendMetrics {
  current: SustainabilityMetrics;
  previous: SustainabilityMetrics;
  percentageChange: {
    waterSaved: number;
    energySaved: number;
    co2Reduced: number;
    cleaningsSkipped: number;
  };
}

export interface DashboardSummary {
  metrics: SustainabilityMetrics; // Historical monthly totals from localStorage
  activeSavings: SustainabilityMetrics; // Current active guests' committed savings from PMS
  participation: ParticipationMetrics;
  trends: TrendMetrics;
  generatedAt: Date;
}

// =============================================================================
// CACHING
// =============================================================================

interface CachedMetrics {
  data: DashboardSummary | null;
  timestamp: number;
}

let metricsCache: CachedMetrics = {
  data: null,
  timestamp: 0,
};

const CACHE_TTL = 60000; // 1 minute

/**
 * Invalidate cache (call when new EcoEvents are created)
 */
export function invalidateSustainabilityCache(): void {
  metricsCache = { data: null, timestamp: 0 };
  console.log('[SustainabilityAggregator] Cache invalidated');
}

// =============================================================================
// DATE UTILITIES
// =============================================================================

function getDateRange(scope: TimeScope): { start: Date; end: Date } {
  const now = new Date();
  let start: Date;
  let end: Date;

  switch (scope) {
    case 'today': {
      start = new Date(now);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      break;
    }
    case 'week': {
      start = new Date(now);
      start.setDate(now.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      // Include future dates within 7 days
      end = new Date(now);
      end.setDate(now.getDate() + 7);
      end.setHours(23, 59, 59, 999);
      break;
    }
    case 'month': {
      start = new Date(now);
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      // Include entire current month (past + future)
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of current month
      end.setHours(23, 59, 59, 999);
      break;
    }
    case 'all': {
      start = new Date(0); // Beginning of time
      end = new Date(2100, 0, 1); // Far future
      break;
    }
  }

  return { start, end };
}

function isEventInRange(event: EcoEvent, start: Date, end: Date): boolean {
  const eventDate = new Date(event.date);
  return eventDate >= start && eventDate <= end;
}

// =============================================================================
// METRICS CALCULATION
// =============================================================================

/**
 * Calculate sustainability savings from EcoEvents
 */
function calculateSavings(events: EcoEvent[]): Omit<SustainabilityMetrics, 'timeRange'> {
  let waterSaved = 0;
  let energySaved = 0;
  let co2Reduced = 0;
  let laundryCyclesAvoided = 0;
  let cleaningsSkipped = 0;

  console.log(`[SustainabilityAggregator] calculateSavings: processing ${events.length} events`);

  for (const event of events) {
    switch (event.action) {
      case 'SKIP_CLEANING': {
        // Full clean avoided
        waterSaved += BASELINE_CONSTANTS.FULL_CLEAN.water;
        energySaved += BASELINE_CONSTANTS.FULL_CLEAN.energy;
        co2Reduced += BASELINE_CONSTANTS.FULL_CLEAN.co2;
        laundryCyclesAvoided += BASELINE_CONSTANTS.FULL_CLEAN.laundry;
        cleaningsSkipped += 1;
        console.log(`[SustainabilityAggregator] SKIP_CLEANING on ${event.date}: +100L water, total now ${waterSaved}L`);
        break;
      }
      case 'TOWELS_ONLY': {
        // Partial clean vs full clean - savings = full - partial
        const savings = {
          water: BASELINE_CONSTANTS.FULL_CLEAN.water - BASELINE_CONSTANTS.TOWELS_ONLY.water,
          energy: BASELINE_CONSTANTS.FULL_CLEAN.energy - BASELINE_CONSTANTS.TOWELS_ONLY.energy,
          co2: BASELINE_CONSTANTS.FULL_CLEAN.co2 - BASELINE_CONSTANTS.TOWELS_ONLY.co2,
          laundry: BASELINE_CONSTANTS.FULL_CLEAN.laundry - BASELINE_CONSTANTS.TOWELS_ONLY.laundry,
        };
        waterSaved += savings.water;
        energySaved += savings.energy;
        co2Reduced += savings.co2;
        laundryCyclesAvoided += savings.laundry;
        console.log(`[SustainabilityAggregator] TOWELS_ONLY on ${event.date}: +30L water savings, total now ${waterSaved}L`);
        break;
      }
      // ECO_ENABLED, ECO_DISABLED don't directly save resources
      // They're opt-in signals but actual savings come from SKIP_CLEANING/TOWELS_ONLY
    }
  }

  console.log(`[SustainabilityAggregator] Final totals: ${waterSaved}L water, ${energySaved}kWh energy, ${co2Reduced}kg CO2, ${cleaningsSkipped} cleanings`);


  // Calculate cost avoided
  const costAvoided =
    waterSaved * COST_ESTIMATES.waterPerLiter +
    energySaved * COST_ESTIMATES.energyPerKwh +
    laundryCyclesAvoided * COST_ESTIMATES.laundryCycle;

  return {
    waterSaved,
    energySaved,
    co2Reduced,
    laundryCyclesAvoided,
    cleaningsSkipped,
    costAvoided,
  };
}

/**
 * Aggregate sustainability metrics for a given time scope
 */
export function aggregateSustainabilityMetrics(scope: TimeScope = 'month'): SustainabilityMetrics {
  try {
    const allEvents = getAllEcoEvents();
    const { start, end } = getDateRange(scope);

    // Filter events in range
    const eventsInRange = allEvents.filter(e => isEventInRange(e, start, end));

    console.log(`[SustainabilityAggregator] Aggregating ${scope}: ${eventsInRange.length} events in range`);

    const savings = calculateSavings(eventsInRange);

    return {
      ...savings,
      timeRange: { start, end, scope },
    };
  } catch (error) {
    console.error('[SustainabilityAggregator] Error aggregating metrics:', error);
    // Return zero metrics on error
    const { start, end } = getDateRange(scope);
    return {
      waterSaved: 0,
      energySaved: 0,
      co2Reduced: 0,
      laundryCyclesAvoided: 0,
      cleaningsSkipped: 0,
      costAvoided: 0,
      timeRange: { start, end, scope },
    };
  }
}

/**
 * Calculate participation metrics
 *
 * IMPORTANT: activeParticipants now fetched from PMS (current guests only),
 * while historical metrics (towelsOnlyAdoption, skipCleaningAdoption) use localStorage.
 */
export function aggregateParticipationMetrics(pmsRooms?: any[]): ParticipationMetrics {
  try {
    const allEvents = getAllEcoEvents();

    // Get CURRENT active participants from PMS (source of truth for live status)
    const activeParticipants = pmsRooms ? countGreenStayParticipants(pmsRooms) : 0;

    // Get unique stays with eco mode enabled (HISTORICAL from localStorage)
    const towelsOnlyStays = new Set<string>();
    const skipCleaningStays = new Set<string>();
    const stayEventCounts = new Map<string, number>();

    for (const event of allEvents) {
      const stayId = event.stayId;

      // Count only real eco actions (not ECO_ENABLED signal) per stay for repeat participation
      if (event.action === 'SKIP_CLEANING' || event.action === 'TOWELS_ONLY') {
        stayEventCounts.set(stayId, (stayEventCounts.get(stayId) || 0) + 1);
      }

      if (event.action === 'TOWELS_ONLY') {
        towelsOnlyStays.add(stayId);
      } else if (event.action === 'SKIP_CLEANING') {
        skipCleaningStays.add(stayId);
      }
    }

    // totalGuests: use occupied PMS rooms as total guests when available (source of truth)
    // Fall back to unique stayIds from localStorage historical events
    const totalGuests = pmsRooms && pmsRooms.length > 0
      ? pmsRooms.filter((r: any) => r.stay).length || 1
      : new Set(allEvents.map(e => e.stayId)).size || 1;

    const participationRate = totalGuests > 0 ? (activeParticipants / totalGuests) * 100 : 0;

    // Repeat participation = stays with 2+ actual eco action days (SKIP or TOWELS, not ECO_ENABLED)
    const repeatParticipation = Array.from(stayEventCounts.values()).filter(count => count >= 2).length;

    console.log(`[SustainabilityAggregator] Participation: ${activeParticipants} active (PMS), ${totalGuests} total occupied rooms`);

    return {
      activeParticipants, // Current from PMS
      totalGuests,
      participationRate,
      towelsOnlyAdoption: towelsOnlyStays.size,
      skipCleaningAdoption: skipCleaningStays.size,
      repeatParticipation,
    };
  } catch (error) {
    console.error('[SustainabilityAggregator] Error aggregating participation:', error);
    return {
      activeParticipants: 0,
      totalGuests: 0,
      participationRate: 0,
      towelsOnlyAdoption: 0,
      skipCleaningAdoption: 0,
      repeatParticipation: 0,
    };
  }
}

/**
 * Calculate trends (current vs previous period)
 */
export function aggregateTrendMetrics(scope: TimeScope = 'week'): TrendMetrics {
  try {
    const current = aggregateSustainabilityMetrics(scope);

    // Calculate previous period
    const { start: currentStart } = current.timeRange;
    const duration = Date.now() - currentStart.getTime();

    const previousStart = new Date(currentStart.getTime() - duration);
    const previousEnd = new Date(currentStart.getTime() - 1);

    const allEvents = getAllEcoEvents();
    const previousEvents = allEvents.filter(e => isEventInRange(e, previousStart, previousEnd));

    const previousSavings = calculateSavings(previousEvents);
    const previous: SustainabilityMetrics = {
      ...previousSavings,
      timeRange: { start: previousStart, end: previousEnd, scope },
    };

    // Calculate percentage changes
    const percentageChange = {
      waterSaved: calculatePercentageChange(previous.waterSaved, current.waterSaved),
      energySaved: calculatePercentageChange(previous.energySaved, current.energySaved),
      co2Reduced: calculatePercentageChange(previous.co2Reduced, current.co2Reduced),
      cleaningsSkipped: calculatePercentageChange(previous.cleaningsSkipped, current.cleaningsSkipped),
    };

    return {
      current,
      previous,
      percentageChange,
    };
  } catch (error) {
    console.error('[SustainabilityAggregator] Error aggregating trends:', error);
    const current = aggregateSustainabilityMetrics(scope);
    return {
      current,
      previous: current, // Same as current on error
      percentageChange: {
        waterSaved: 0,
        energySaved: 0,
        co2Reduced: 0,
        cleaningsSkipped: 0,
      },
    };
  }
}

function calculatePercentageChange(previous: number, current: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

/**
 * Get comprehensive dashboard summary with caching
 */
export async function getDashboardSummary(forceRefresh = false): Promise<DashboardSummary> {
  const now = Date.now();

  // Return cached data if valid
  if (!forceRefresh && metricsCache.data && now - metricsCache.timestamp < CACHE_TTL) {
    console.log('[SustainabilityAggregator] Returning cached dashboard summary');
    return metricsCache.data;
  }

  console.log('[SustainabilityAggregator] Computing fresh dashboard summary');

  try {
    // Fetch PMS rooms ONCE and pass to both savings and participation
    let pmsRooms: any[] = [];
    try {
      const pmsResp = await fetch(`${API_BASE}/api/pms-sandbox/rooms`);
      if (pmsResp.ok) {
        const pmsData = await pmsResp.json();
        pmsRooms = pmsData.data || [];
      }
    } catch (err) {
      console.error('[SustainabilityAggregator] Failed to fetch PMS rooms:', err);
    }

    const metrics = aggregateSustainabilityMetrics('month');
    const activeSavings = computeActiveSavings(pmsRooms); // Active guests' committed savings
    const participation = aggregateParticipationMetrics(pmsRooms); // Sync with pre-fetched rooms
    const trends = aggregateTrendMetrics('week');

    const summary: DashboardSummary = {
      metrics,
      activeSavings,
      participation,
      trends,
      generatedAt: new Date(),
    };

    // Cache the result
    metricsCache = {
      data: summary,
      timestamp: now,
    };

    return summary;
  } catch (error) {
    console.error('[SustainabilityAggregator] Error generating dashboard summary:', error);
    // Return empty summary on error
    const emptyMetrics = aggregateSustainabilityMetrics('month');
    return {
      metrics: emptyMetrics,
      activeSavings: getZeroMetrics(),
      participation: {
        activeParticipants: 0,
        totalGuests: 0,
        participationRate: 0,
        towelsOnlyAdoption: 0,
        skipCleaningAdoption: 0,
        repeatParticipation: 0,
      },
      trends: {
        current: emptyMetrics,
        previous: emptyMetrics,
        percentageChange: {
          waterSaved: 0,
          energySaved: 0,
          co2Reduced: 0,
          cleaningsSkipped: 0,
        },
      },
      generatedAt: new Date(),
    };
  }
}

// =============================================================================
// CSV EXPORT
// =============================================================================

export interface CSVExportRow {
  date: string;
  cleaningsSkipped: number;
  waterSaved: number;
  energySaved: number;
  co2Reduced: number;
}

/**
 * Generate CSV export data for management reporting
 */
export function generateCSVExport(scope: TimeScope = 'month'): string {
  try {
    const allEvents = getAllEcoEvents();
    const { start, end } = getDateRange(scope);

    // Group events by date
    const eventsByDate = new Map<string, EcoEvent[]>();

    for (const event of allEvents) {
      if (isEventInRange(event, start, end)) {
        const dateKey = event.date;
        if (!eventsByDate.has(dateKey)) {
          eventsByDate.set(dateKey, []);
        }
        eventsByDate.get(dateKey)!.push(event);
      }
    }

    // Generate CSV rows
    const rows: CSVExportRow[] = [];

    for (const [date, events] of eventsByDate.entries()) {
      const savings = calculateSavings(events);
      rows.push({
        date,
        cleaningsSkipped: savings.cleaningsSkipped,
        waterSaved: Math.round(savings.waterSaved),
        energySaved: Math.round(savings.energySaved * 10) / 10,
        co2Reduced: Math.round(savings.co2Reduced * 10) / 10,
      });
    }

    // Sort by date
    rows.sort((a, b) => a.date.localeCompare(b.date));

    // Generate CSV string
    const header = 'Date,Cleanings Skipped,Water Saved (L),Energy Saved (kWh),CO2 Reduced (kg)\n';
    const csvRows = rows.map(
      row => `${row.date},${row.cleaningsSkipped},${row.waterSaved},${row.energySaved},${row.co2Reduced}`
    );

    return header + csvRows.join('\n');
  } catch (error) {
    console.error('[SustainabilityAggregator] Error generating CSV export:', error);
    return 'Date,Cleanings Skipped,Water Saved (L),Energy Saved (kWh),CO2 Reduced (kg)\nError generating report';
  }
}

/**
 * Download CSV file
 */
export function downloadCSVExport(scope: TimeScope = 'month'): void {
  const csv = generateCSVExport(scope);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `sustainability-impact-${scope}-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  console.log('[SustainabilityAggregator] CSV export downloaded');
}
